import { createContext, useContext, useState, ReactNode } from 'react';
import { LocationModal } from '../components/providers';
import useProviderProfileStore from '../stores/providerProfileStore';
import type { ProviderLocation } from '../types/provider';

interface LocationModalContextType {
	openAddLocation: () => void;
	openEditLocation: (location: ProviderLocation) => void;
	closeModal: () => void;
}

const LocationModalContext = createContext<LocationModalContextType | null>(null);

export const useLocationModal = () => {
	const context = useContext(LocationModalContext);
	if (!context) {
		throw new Error('useLocationModal must be used within a LocationModalProvider');
	}
	return context;
};

interface LocationModalProviderProps {
	children: ReactNode;
}

export const LocationModalProvider = ({ children }: LocationModalProviderProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [editingLocation, setEditingLocation] = useState<ProviderLocation | null>(null);
	const { fetchProfile } = useProviderProfileStore();

	const openAddLocation = () => {
		setEditingLocation(null);
		setIsOpen(true);
	};

	const openEditLocation = (location: ProviderLocation) => {
		setEditingLocation(location);
		setIsOpen(true);
	};

	const closeModal = () => {
		setIsOpen(false);
		setEditingLocation(null);
	};

	const handleSuccess = () => {
		fetchProfile();
	};

	return (
		<LocationModalContext.Provider value={{ openAddLocation, openEditLocation, closeModal }}>
			{children}
			<LocationModal
				isOpen={isOpen}
				onClose={closeModal}
				location={editingLocation}
				onSuccess={handleSuccess}
			/>
		</LocationModalContext.Provider>
	);
};
